import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Read values directly from DOM to handle mobile autofill
      // (autofill doesn't always trigger React onChange)
      const formEmail = e.target.querySelector('input[type="email"]')?.value || email;
      const formPassword = e.target.querySelector('input[type="password"], input[id="password"]')?.value || password;

      if (!formEmail || !formPassword) {
        setError('Please enter your email and password');
        setLoading(false);
        return;
      }

      await login(formEmail, formPassword);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid email or password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img src="/logo-nav.png" alt="Career Step" className="h-10 w-auto" />
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Career Step</span>
          </Link>

          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('login.title')}
          </h1>
          <p className="text-slate-600 mb-8">
            {t('login.subtitle')}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 h-12 rounded-lg border-slate-200"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-slate-700 font-medium">{t('login.password')}</Label>
                <Link to="/forgot-password" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 rounded-lg border-slate-200 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('login.submitting')}
                </>
              ) : (
                t('login.submit')
              )}
            </Button>
          </form>

          <p className="text-center text-slate-600 mt-6">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-slate-900 font-semibold hover:underline">
              {t('login.signUp')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 bg-slate-900 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-slate-900/70 z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1577948000111-9c970dfe3743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxqb2hhbm5lc2J1cmclMjBzb3V0aCUyMGFmcmljYSUyMGNpdHklMjBza3lsaW5lJTIwbW9kZXJufGVufDB8fHx8MTc2OTcxMTU4Nnww&ixlib=rb-4.1.0&q=90&w=1600"
          alt="Johannesburg skyline"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <blockquote className="text-white text-xl italic mb-4">
            "{t('login.testimonial')}"
          </blockquote>
          <cite className="text-amber-400 font-semibold">{t('login.testimonialAuthor')}</cite>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
